// LT-341: typed client for /agents REST API. Mirrors the contactsAPI /
// accountsAPI conventions — minimal axios instance with the bearer token
// interceptor, no transform of the response shape.

import axios from 'axios';

import {
  Agent,
  AgentCreate,
  AgentListFilters,
  AgentPinnedByStep,
  AgentUpdate,
  AgentVersion,
} from '../types/agent';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

function buildParams(filters?: AgentListFilters): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  if (!filters) return out;
  if (filters.category) out.category = filters.category;
  if (typeof filters.includeSystem === 'boolean') out.include_system = filters.includeSystem;
  if (typeof filters.includeDeprecated === 'boolean')
    out.include_deprecated = filters.includeDeprecated;
  return out;
}

export const agentsService = {
  async list(filters?: AgentListFilters): Promise<Agent[]> {
    const res = await client.get<Agent[]>('/agents', { params: buildParams(filters) });
    return res.data;
  },

  async get(id: string): Promise<Agent> {
    const res = await client.get<Agent>(`/agents/${id}`);
    return res.data;
  },

  async listVersions(id: string): Promise<AgentVersion[]> {
    const res = await client.get<AgentVersion[]>(`/agents/${id}/versions`);
    return res.data;
  },

  // LT-384: which sequence_steps currently pin a version of this Agent.
  // Empty list when nothing is pinned.
  async listPinnedBy(id: string): Promise<AgentPinnedByStep[]> {
    const res = await client.get<AgentPinnedByStep[]>(`/agents/${id}/pinned-by`);
    return res.data;
  },

  async create(payload: AgentCreate): Promise<Agent> {
    const res = await client.post<Agent>('/agents', payload);
    return res.data;
  },

  // PUT /agents/{id}. Send only the fields you want changed — omitted
  // fields inherit from the current version. LT-385: metadata-only
  // edits (name/description) skip the version bump on the backend.
  async update(id: string, payload: AgentUpdate): Promise<Agent> {
    const res = await client.put<Agent>(`/agents/${id}`, payload);
    return res.data;
  },

  async deprecate(id: string): Promise<void> {
    await client.delete(`/agents/${id}`);
  },
};

export default agentsService;
